import { useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { colors } from '@/theme/tokens';
import { redirectService } from './redirect-service';

/**
 * Callback page that Auth0 redirects to after login.
 * Auth0Provider handles the token exchange here, then we redirect to the app.
 */
export function CallbackPage() {
  const { isAuthenticated, isLoading, error, loginWithRedirect } = useAuth0();
  const navigate = useNavigate();

  useEffect(() => {
    if (isLoading) {
      console.log('[Callback] Still loading…');
      return;
    }

    console.log('[Callback] State:', { isAuthenticated, error: error?.message });

    if (error) {
      console.error('[Callback] Auth error:', error);
      // Don't auto-navigate — let the error UI render so the user can see what happened.
      return;
    }

    if (isAuthenticated) {
      console.log('[Callback] Auth successful, redirecting…');
      const returnTo = redirectService.consume();
      navigate(returnTo || '/admin/dashboard', { replace: true });
      return;
    }

    // Not loading, no error, not authenticated — the callback didn't produce a session.
    // Do NOT navigate to /admin (that triggers loginWithRedirect → loop).
    console.warn('[Callback] Finished loading but not authenticated. Showing error.');
  }, [isAuthenticated, isLoading, error, navigate]);

  // Show error or "callback failed silently" — both are terminal states, no auto-redirect.
  if (!isLoading && (error || !isAuthenticated)) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          bgcolor: colors.bg,
          gap: 2,
          p: 3,
        }}
      >
        <Typography variant="h6" color="error">
          {error ? 'Authentication failed' : 'Sign-in did not complete'}
        </Typography>
        <Typography variant="body2" color="text.secondary" textAlign="center">
          {error ? error.message : 'The sign-in callback finished but no session was created.'}
        </Typography>
        <Button
          variant="contained"
          onClick={() => void loginWithRedirect()}
          sx={{
            mt: 2,
            bgcolor: colors.brass,
            '&:hover': { bgcolor: colors.brassHover },
          }}
        >
          Try Again
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        bgcolor: colors.bg,
        gap: 2,
      }}
    >
      <CircularProgress sx={{ color: colors.accent }} />
      <Typography variant="body2" color="text.secondary">
        Completing sign in...
      </Typography>
    </Box>
  );
}
